import { Check, ChevronDown, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCustomPackages } from '@/api/domains/custom-packages';
import { useMe } from '@/api/domains/merchant-settings';
import { useUpdateOrderSelectedPoint } from '@/api/domains/order-shipments';
import { packageFitsLimits } from '@/api/domains/package-templates';
import { useCreateShipment } from '@/api/domains/shipments';
import type { Money, SendingMethod, ServiceType } from '@/api/types/common';
import type { CustomPackage } from '@/api/types/custom-packages';
import type { Order } from '@/api/types/orders';
import type { PackageTemplate, ParcelLimits } from '@/api/types/package-templates';
import type { Shipment } from '@/api/types/shipments';
import { PointSearchIcon } from '@/components/icons/shipment-icons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

import { LabelFormatDialog } from '../shipments/LabelFormatDialog';
import { StatusBadge } from '../shipments/StatusBadge';
import { PointMapDialog } from './PointMapDialog';

const ALL_DELIVERY_METHODS: ServiceType[] = ['locker', 'pudo', 'to_door', 'to_door_c2c'];
const ALL_SENDING_METHODS: SendingMethod[] = ['courier', 'parcel_locker', 'pudo', 'hub'];

/**
 * The delivery select offers the service types (including the C2C courier
 * service) plus the cash-on-delivery variants; a COD option maps to the same
 * base delivery side and additionally sends `cash_on_delivery: true` (the
 * courier collects the order total). The COD variants render in their own
 * labeled group at the bottom of the select.
 */
type DeliveryOption = ServiceType | 'locker_cod' | 'to_door_cod' | 'to_door_c2c_cod';

const PLAIN_DELIVERY_OPTIONS: DeliveryOption[] = ['locker', 'pudo', 'to_door', 'to_door_c2c'];

const COD_DELIVERY_OPTIONS: DeliveryOption[] = ['locker_cod', 'to_door_cod', 'to_door_c2c_cod'];

function baseServiceType(option: DeliveryOption): ServiceType {
  switch (option) {
    case 'locker_cod':
      return 'locker';
    case 'to_door_cod':
      return 'to_door';
    case 'to_door_c2c_cod':
      return 'to_door_c2c';
    default:
      return option;
  }
}

function isCodOption(option: DeliveryOption): boolean {
  return option === 'locker_cod' || option === 'to_door_cod' || option === 'to_door_c2c_cod';
}

/** The COD variant of a service type; PUDO has no COD variant and stays plain. */
function codOptionFor(serviceType: ServiceType): DeliveryOption {
  switch (serviceType) {
    case 'locker':
      return 'locker_cod';
    case 'to_door':
      return 'to_door_cod';
    case 'to_door_c2c':
      return 'to_door_c2c_cod';
    default:
      return serviceType;
  }
}

/** Whether the service delivers to the recipient's address (no pickup point). */
function deliversToDoor(serviceType: ServiceType): boolean {
  return serviceType === 'to_door' || serviceType === 'to_door_c2c';
}

const fieldLabelClasses = 'text-sm font-medium leading-none text-black';
const selectTriggerClasses =
  'h-9 rounded-[10px] border-[#e5e5e5] bg-white px-3 py-2 text-[#737373] shadow-none focus:ring-0 [&>span]:truncate';
const yellowButtonClasses =
  'flex h-9 w-[159px] items-center justify-center rounded-[10px] bg-primary px-4 text-sm font-medium leading-5 text-black hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50';

function packageLabel(
  pkg: Pick<PackageTemplate, 'name' | 'length' | 'width' | 'height' | 'dimension_unit'>,
): string {
  return `${pkg.name} - ${pkg.height} x ${pkg.width} x ${pkg.length} ${pkg.dimension_unit.toLowerCase()}`;
}

/** Widest the package tooltip gets (max-w-xs) plus its offset and arrow. */
const PACKAGE_TOOLTIP_WIDTH_PX = 340;

type TooltipSide = 'left' | 'right' | 'top';

/**
 * The package tooltip sits to the LEFT of the option row by default; when the
 * dropdown hugs the left edge of the viewport it moves right, and when neither
 * side has room it goes above (Radix still flips top→bottom on its own).
 * Radix alone only flips to the opposite side, hence the manual pick.
 */
function preferredTooltipSide(trigger: DOMRect, viewportWidth: number): TooltipSide {
  if (trigger.left >= PACKAGE_TOOLTIP_WIDTH_PX) return 'left';
  if (viewportWidth - trigger.right >= PACKAGE_TOOLTIP_WIDTH_PX) return 'right';
  return 'top';
}

/**
 * A package option that greys out with an explanatory tooltip when it does
 * not fit the selected delivery method. Disabled items swallow hover
 * (pointer-events-none), so - like the disabled login tab on /authorization -
 * the tooltip listens on a wrapper span instead.
 */
function PackageOption({
  pkg,
  disabledReason,
}: {
  pkg: Pick<PackageTemplate, 'key' | 'name' | 'length' | 'width' | 'height' | 'dimension_unit'>;
  disabledReason: string | null;
}) {
  const [side, setSide] = useState<TooltipSide>('left');
  // Measured on every hover/focus (the dropdown may have scrolled or moved),
  // before Radix's open delay elapses, so the bubble opens on the right side.
  const measureSide = (event: React.SyntheticEvent<HTMLSpanElement>) =>
    setSide(preferredTooltipSide(event.currentTarget.getBoundingClientRect(), window.innerWidth));
  const item = (
    <SelectItem value={pkg.key} disabled={disabledReason !== null}>
      {packageLabel(pkg)}
    </SelectItem>
  );
  if (disabledReason === null) return item;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block" tabIndex={0} onPointerEnter={measureSide} onFocus={measureSide}>
          {item}
        </span>
      </TooltipTrigger>
      <TooltipContent side={side} className="max-w-xs">
        {disabledReason}
      </TooltipContent>
    </Tooltip>
  );
}

/** Read-only control that mimics the figma select look (muted value + chevron). */
function StaticSelect({ value }: { value: string }) {
  return (
    <div className="flex h-9 w-full items-center gap-2 rounded-[10px] border border-[#e5e5e5] bg-white px-3">
      <span className="min-w-0 flex-1 truncate text-sm leading-5 text-[#737373]">{value}</span>
      <ChevronDown className="size-4 shrink-0 opacity-50" />
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <span className={fieldLabelClasses}>{label}</span>
      {children}
    </div>
  );
}

/**
 * Grey order-view box (figma): one per shipment, or a draft form when the
 * shipment is not created yet. Draft boxes post to the existing
 * `POST /admin/orders/{id}/shipments` endpoint.
 */
export function ShipmentBox({
  order,
  shipment,
  canUpdateOrderPoint,
  allowedMethods,
  packageTemplates = [],
  parcelLimits = {},
  dropOffLimits = {},
  orderCashOnDelivery = false,
  orderC2c = false,
  orderCodCurrency = null,
  orderInsuranceCurrency = null,
  orderCodAvailable = true,
  orderInsuranceAvailable = true,
  orderHasShipments = false,
  orderCodCollected = null,
  onCreated,
  onRemove,
  highlight = false,
}: {
  order: Order;
  shipment: Shipment | null;
  /** Whether the order's checkout delivery method is a COD one (preselects the COD variant). */
  orderCashOnDelivery?: boolean;
  /** Whether the order's checkout delivery method is a C2C one (preselects the C2C delivery service). */
  orderC2c?: boolean;
  /**
   * Backend-resolved currency COD is collected in at the order's destination -
   * may differ from the order currency (e.g. PLN for PL deliveries of an
   * EUR-selling shop; nothing can auto-convert the total then).
   */
  orderCodCurrency?: string | null;
  /**
   * Backend-resolved currency insurance is charged in at the order's
   * destination (destination-based like COD; falls back to the account-level
   * home-market currency for older backends).
   */
  orderInsuranceCurrency?: string | null;
  /**
   * Whether InPost collects cash on delivery on the order's corridor
   * (backend-resolved; false for PL→AT/HU/SK/CZ). The COD delivery options
   * render disabled and the order's COD-ness no longer preselects one.
   */
  orderCodAvailable?: boolean;
  /**
   * Whether InPost insures parcels on the order's corridor (backend-resolved;
   * false for PL→AT/HU/SK/CZ). The insurance input is disabled and the create
   * sends an explicit zero so the account default cannot attach server-side.
   */
  orderInsuranceAvailable?: boolean;
  /**
   * Whether the order already carries a non-cancelled shipment, making this
   * draft a split parcel: the COD amount stops being prefilled with the order
   * total (this parcel collects its own share) and has to be typed.
   */
  orderHasShipments?: boolean;
  /**
   * Cash on delivery the order's existing shipments already collect (summed;
   * null when none) - creating another COD shipment confirms first, because
   * the buyer then pays both amounts at delivery.
   */
  orderCodCollected?: Money | null;
  /**
   * Sending methods available for this order keyed by delivery method
   * (corridor-dependent, resolved backend-side on the detail endpoint). A
   * delivery method with an empty list is unavailable; all lists empty = no
   * corridor info.
   */
  allowedMethods: Partial<Record<ServiceType, SendingMethod[]>>;
  /**
   * Predefined package sizes offered for the order's destination market
   * (backend-resolved; empty for markets without predefined sizes).
   */
  packageTemplates?: PackageTemplate[];
  /**
   * Maximum parcel size per delivery service at the order's destination
   * (backend-resolved). A package exceeding the selected method's limit is
   * greyed out; a service with a null/absent entry enforces none.
   */
  parcelLimits?: Partial<Record<ServiceType, ParcelLimits | null>>;
  /**
   * Maximum parcel size the merchant can hand to a point in the origin
   * country, keyed by sending method (backend-resolved; courier pickup = no
   * cap). Gates the package options together with `parcelLimits`.
   */
  dropOffLimits?: Partial<Record<SendingMethod, ParcelLimits | null>>;
  /**
   * Whether changing the point here should update the order's selected point.
   * True only for the first shipment (order has none yet); for split shipments
   * and already-created ones the point is used per-shipment without touching the
   * order's selection.
   */
  canUpdateOrderPoint: boolean;
  /** Called with the created shipment so the page can count it before the refetch lands. */
  onCreated?: (shipment: Shipment) => void;
  /**
   * Removes this (not-yet-created) split-shipment draft box. Only passed for
   * extra split drafts; the first box and already-created shipments are not
   * removable.
   */
  onRemove?: () => void;
  /** Scroll this box into view and briefly flash it (deep-link from the listing). */
  highlight?: boolean;
}) {
  const { t } = useTranslation('orders');
  const tShipments = useTranslation('shipments').t;
  const tCommon = useTranslation('common').t;

  const customPackages = useCustomPackages();
  const createMutation = useCreateShipment(order.id);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [codPaidConfirmOpen, setCodPaidConfirmOpen] = useState(false);
  const updatePoint = useUpdateOrderSelectedPoint(order.id);

  const [templateKey, setTemplateKey] = useState('');
  // No corridor info at all (backend could not resolve any route): fail open
  // with everything enabled - creation would fail anyway, mirroring the old
  // full-list fallback.
  const hasCorridorInfo = ALL_DELIVERY_METHODS.some(
    (method) => (allowedMethods[method] ?? []).length > 0,
  );
  const sendingMethodsFor = useCallback(
    (delivery: ServiceType): SendingMethod[] =>
      hasCorridorInfo ? (allowedMethods[delivery] ?? []) : ALL_SENDING_METHODS,
    [allowedMethods, hasCorridorInfo],
  );
  const me = useMe().data;
  // Merchant-level default dispatch method (Settings → General → Shipping
  // defaults); preferred over the first allowed method when the corridor allows it.
  const defaultSendingSetting = me?.settings?.default_sending_method ?? null;
  // Merchant-level default package size: a predefined slug or `custom:{id}`.
  // Applied only while it resolves to an existing option - a stale key (e.g. a
  // custom package deleted on another device) must fall back to the predefined
  // default, not 422 the create.
  const defaultPackageSetting = me?.settings?.default_package_key ?? null;
  // Insurance is charged in the DESTINATION-resolved currency the backend
  // provides per order (PLN for PL deliveries, EUR elsewhere) - never the shop
  // currency; the me-level value is only an indicative deploy-skew fallback.
  // The draft is prefilled with the account default and always sent on create
  // (0 = none).
  const insuranceCurrency = orderInsuranceCurrency ?? me?.merchant.insurance_currency ?? null;
  const defaultInsuranceValue = me?.settings?.default_insurance_value ?? null;
  const [insuranceDraft, setInsuranceDraft] = useState('');
  const insuranceTouchedRef = useRef(false);
  // The settings query may resolve after mount: keep prefilling the default
  // until the merchant edits the field.
  useEffect(() => {
    if (insuranceTouchedRef.current) return;
    setInsuranceDraft(
      !orderInsuranceAvailable || defaultInsuranceValue == null
        ? ''
        : String(defaultInsuranceValue),
    );
  }, [defaultInsuranceValue, orderInsuranceAvailable]);
  // The currency the courier collects COD in at the destination (backend
  // resolved; falls back to the order currency for older backends).
  const codCurrency = orderCodCurrency ?? order.shopify_currency ?? null;
  const codCurrencyMismatch = Boolean(
    codCurrency && order.shopify_currency && codCurrency !== order.shopify_currency,
  );
  // A collection this page never saw: the order gained a collecting shipment
  // after the detail query cached it (queries stay fresh for minutes and never
  // refetch on focus), so the create came back with `cod_already_collected`
  // naming what it collects. From here the box behaves like any split draft -
  // blank amount, no order-total fallback - behind the same confirm.
  const [extraCodProblem, setExtraCodProblem] = useState<string | null>(null);
  // The amount the merchant acknowledged an extra collection FOR - they read
  // "adding X means the buyer pays Y in total", so the consent covers that X
  // and nothing else. Editing the amount brings the question back.
  const confirmedExtraCodAmountRef = useRef<string | null>(null);
  // The settled-payment answer is a plain yes/no about the order, not an
  // amount. Both live in refs because the two guards chain and a retry must not
  // drop the earlier answer.
  const forceCodRef = useRef(false);
  // This draft is one parcel of several: it collects its own share, so it never
  // offers the order total.
  const isSplitDraft = orderHasShipments || extraCodProblem !== null;
  // ...and something else on the order already collects, so creating this one
  // adds to what the buyer pays at the door.
  const addsExtraCollection = orderCodCollected !== null || extraCodProblem !== null;
  // Default COD amount for a draft: the order total, i.e. what a single
  // shipment collects. It is left blank - the merchant types it - when the
  // destination collects in another currency (a 200 EUR total must not become
  // "200 PLN") or when the order already has a shipment, because this parcel
  // then collects its own share, not the whole order again.
  const defaultCodAmount =
    codCurrencyMismatch || isSplitDraft ? '' : (order.shopify_total_price ?? '');
  const [codDraft, setCodDraft] = useState(defaultCodAmount);
  const codTouchedRef = useRef(false);
  // The default can change while the box is open - a sibling draft created a
  // shipment, so this one became a split and must stop offering the order
  // total. Only an untouched field follows it; a typed amount is the
  // merchant's and is left alone (the currency reset below owns that case).
  useEffect(() => {
    if (codTouchedRef.current || codDraft === defaultCodAmount) return;
    // Losing a visible amount without a word is the one thing every other COD
    // path in this file avoids; the merchant has to know the field emptied
    // because the order gained a shipment, not because they missed it.
    if (codDraft !== '' && defaultCodAmount === '' && !codCurrencyMismatch) {
      toast.info(t('detail.codAmountClearedForSplit'));
    }
    setCodDraft(defaultCodAmount);
  }, [defaultCodAmount, codDraft, codCurrencyMismatch, t]);
  const [codAmountConfirmOpen, setCodAmountConfirmOpen] = useState(false);
  // Another collection on an order that already collects: asked about before
  // the create, the buyer would otherwise pay both amounts unannounced.
  const collectedCodAmount = Number(orderCodCollected?.amount ?? 0);
  const [codExtraConfirmOpen, setCodExtraConfirmOpen] = useState(false);
  // The resolved currency can change after mount (the details query refetches
  // in place, e.g. after creating a sibling split shipment): a draft prefilled
  // under the previous currency must never survive into the new one - it
  // would pair a stale number with the freshly-resolved currency.
  const prevCodCurrencyRef = useRef(codCurrency);
  useEffect(() => {
    if (prevCodCurrencyRef.current === codCurrency) return;
    prevCodCurrencyRef.current = codCurrency;
    const hadDraft = codDraft.trim() !== '';
    // This overwrites whatever the merchant typed, so the field is back on the
    // default and must follow it again - a ref left set here would pin a stale
    // total past the point the order becomes a split.
    codTouchedRef.current = false;
    setCodDraft(defaultCodAmount);
    // A money field must never change under the merchant without a word -
    // every other COD divergence in this file toasts, so does this one.
    if (hadDraft) toast.info(t('detail.codAmountReset'));
  }, [codCurrency, defaultCodAmount, codDraft, t]);
  // Unknown collection currency (no backend value and no order currency): an
  // amount typed here could not be sent (the payload requires the currency
  // echo), so COD creates are blocked instead of silently collecting the
  // backend-derived default.
  const codCurrencyUnknown = codCurrency === null;
  const preferredSendingFor = useCallback(
    (delivery: ServiceType): SendingMethod => {
      const allowed = sendingMethodsFor(delivery);
      if (
        defaultSendingSetting &&
        (allowed.length === 0 || allowed.includes(defaultSendingSetting))
      ) {
        return defaultSendingSetting;
      }
      return allowed[0] ?? ALL_SENDING_METHODS[0];
    },
    [sendingMethodsFor, defaultSendingSetting],
  );
  // The service the order's checkout method implies for its shipments: rate
  // codes only carry base types, so a C2C order upgrades `to_door` to the
  // dedicated C2C service here (mirrors the backend's effective service type).
  const orderServiceType: ServiceType =
    orderC2c && (order.service_type ?? 'to_door') === 'to_door'
      ? 'to_door_c2c'
      : (order.service_type ?? 'to_door');
  const [deliveryOption, setDeliveryOption] = useState<DeliveryOption>(() =>
    orderCashOnDelivery && orderCodAvailable ? codOptionFor(orderServiceType) : orderServiceType,
  );
  const deliveryMethod = baseServiceType(deliveryOption);
  // Every package is checked against the selected delivery method's limit at
  // the order's destination (backend-enforced too): options that don't fit
  // render disabled with the reason, and a pick/default that stops fitting
  // (or stops existing - custom package deleted mid-session) falls back to the
  // first fitting option instead of sending a doomed key. The merchant's own
  // pick returns when they switch delivery back to a method it fits.
  const packageLimits = parcelLimits[deliveryMethod] ?? null;
  // The predefined list is already per destination (unique slugs); custom
  // packages follow, destination-independent.
  const packageOptions = useMemo(
    () => [...packageTemplates, ...(customPackages.data ?? [])],
    [packageTemplates, customPackages.data],
  );
  const [sendingMethod, setSendingMethod] = useState<SendingMethod>(() =>
    preferredSendingFor(orderServiceType),
  );
  // Second gate: a parcel the merchant hands to a locker/PUDO/HUB must also fit
  // that point, whatever the delivery method (courier pickup has no cap).
  const dropOffPackageLimits = dropOffLimits[sendingMethod] ?? null;
  const packageFits = useCallback(
    (key: string) => {
      const pkg = packageOptions.find((option) => option.key === key);
      return (
        pkg !== undefined &&
        packageFitsLimits(pkg, packageLimits) &&
        packageFitsLimits(pkg, dropOffPackageLimits)
      );
    },
    [packageOptions, packageLimits, dropOffPackageLimits],
  );
  const packageTooBigReason = (pkg: PackageTemplate | CustomPackage): string | null => {
    if (packageLimits && !packageFitsLimits(pkg, packageLimits)) {
      return t('detail.packageTooBig', {
        method: tShipments(`serviceTypes.${deliveryMethod}`),
        length: packageLimits.length,
        width: packageLimits.width,
        height: packageLimits.height,
        weight: packageLimits.max_weight_kg,
      });
    }
    if (dropOffPackageLimits && !packageFitsLimits(pkg, dropOffPackageLimits)) {
      return t('detail.packageTooBigForDropOff', {
        length: dropOffPackageLimits.length,
        width: dropOffPackageLimits.width,
        height: dropOffPackageLimits.height,
        weight: dropOffPackageLimits.max_weight_kg,
      });
    }
    return null;
  };
  const selectedPackageKey = useMemo(() => {
    // The merchant's pick, then the account default (Settings → General), then
    // the destination's predefined default, then whatever fits first.
    const candidates = [
      templateKey,
      defaultPackageSetting ?? '',
      packageTemplates.find((template) => template.is_default)?.key ?? '',
      ...packageOptions.map((option) => option.key),
    ];
    return candidates.find((key) => key !== '' && packageFits(key)) ?? '';
  }, [templateKey, defaultPackageSetting, packageTemplates, packageOptions, packageFits]);
  // Nothing selectable at all: either the destination offers no sizes and the
  // merchant defined none, or every option exceeds the method's limit.
  const noPackageAvailable = !customPackages.isLoading && selectedPackageKey === '';
  // An explicit pick that stopped fitting (delivery/sending method changed) is
  // swapped for the first fitting option - say so, the way the COD amount
  // reset does: a parcel size must never change under the merchant silently.
  const prevSelectedPackageRef = useRef(selectedPackageKey);
  useEffect(() => {
    const previous = prevSelectedPackageRef.current;
    prevSelectedPackageRef.current = selectedPackageKey;
    if (previous === selectedPackageKey || !templateKey || selectedPackageKey === templateKey) {
      return;
    }
    const swappedTo = packageOptions.find((option) => option.key === selectedPackageKey);
    if (swappedTo) {
      toast.info(t('detail.packageAutoSwapped', { package: packageLabel(swappedTo) }));
    }
  }, [selectedPackageKey, templateKey, packageOptions, t]);
  // The settings query may resolve after mount: apply the merchant default
  // until the merchant explicitly picks a sending method.
  const sendingTouchedRef = useRef(false);
  useEffect(() => {
    if (sendingTouchedRef.current || !defaultSendingSetting) return;
    setSendingMethod(preferredSendingFor(deliveryMethod));
  }, [defaultSendingSetting, deliveryMethod, preferredSendingFor]);
  const changeDeliveryOption = (option: DeliveryOption) => {
    setDeliveryOption(option);
    // Keep the sending method valid for the new delivery side.
    const allowed = sendingMethodsFor(baseServiceType(option));
    if (allowed.length > 0 && !allowed.includes(sendingMethod)) {
      setSendingMethod(
        sendingTouchedRef.current ? allowed[0] : preferredSendingFor(baseServiceType(option)),
      );
    }
  };
  const codInputRef = useRef<HTMLInputElement>(null);
  const [pointDraft, setPointDraft] = useState(order.selected_point_id ?? '');
  const [mapOpen, setMapOpen] = useState(false);

  const boxRef = useRef<HTMLDivElement>(null);
  const [flash, setFlash] = useState(false);

  // Deep-linked from the shipments listing: scroll the targeted shipment into
  // view and briefly highlight it.
  useEffect(() => {
    if (!highlight) return;
    boxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), 2000);
    return () => clearTimeout(timer);
  }, [highlight]);

  // The box doesn't remount when the order refetches (stable key), so re-sync
  // the draft when the order's selected point changes - e.g. after the map
  // picker or a manual apply lands and the detail query refetches.
  useEffect(() => {
    setPointDraft(order.selected_point_id ?? '');
  }, [order.selected_point_id]);

  const isDraft = shipment === null;
  // Drafts follow the selected delivery method; created shipments follow their
  // own persisted service type (may differ from the order's after an override).
  const requiresPoint = isDraft
    ? !deliversToDoor(deliveryMethod)
    : !deliversToDoor(shipment.service_type);
  const shipmentDeliveryDisplay = (() => {
    if (isDraft) return '';
    // A shipment that actually carries COD shows the COD variant label; one
    // that doesn't must not show the order's COD rate title.
    if (
      shipment.cod &&
      (shipment.service_type === 'locker' || deliversToDoor(shipment.service_type))
    ) {
      return tShipments(`serviceTypes.${shipment.service_type}_cod`);
    }
    if (shipment.service_type !== orderServiceType || (orderCashOnDelivery && !shipment.cod)) {
      return tShipments(`serviceTypes.${shipment.service_type}`);
    }
    return (
      order.shipping_method_title ??
      (order.service_type ? tShipments(`serviceTypes.${orderServiceType}`) : '-')
    );
  })();
  // The order's selected point only tracks the buyer-chosen delivery method; a
  // point picked for an overridden delivery method stays per-shipment.
  const pointUpdatesOrder = canUpdateOrderPoint && deliveryMethod === order.service_type;

  const applyPoint = () => {
    const pointId = pointDraft.trim();
    if (!pointId || pointId === order.selected_point_id) return;

    updatePoint.mutate(
      { point_id: pointId },
      {
        onSuccess: () => toast.success(t('detail.pointUpdated')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  // A non-blank draft that isn't a finite non-negative number must block the
  // create (with visible feedback) - silently coercing it to 0 would ship the
  // parcel uninsured behind the merchant's back.
  const insuranceDraftInvalid = (() => {
    const raw = insuranceDraft.trim();
    if (raw === '') return false;
    const parsed = Number(raw);
    return !Number.isFinite(parsed) || parsed < 0;
  })();

  // Explicit insurance for the create payload: the visible draft is the truth
  // (0 when blank, so the backend default can't sneak back in behind the
  // merchant's back). Omitted only while the currency is still unknown
  // (settings not loaded) - the backend default then applies server-side.
  const insuranceForPayload = (): { value: number; currency: string } | undefined => {
    if (!insuranceCurrency) return undefined;
    // No cover on this corridor: an explicit zero keeps the backend from
    // attaching the account default (it skips it anyway, but the payload
    // should say what the form shows).
    if (!orderInsuranceAvailable) return { value: 0, currency: insuranceCurrency };
    const parsed = Number(insuranceDraft.trim());
    const value = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    return { value, currency: insuranceCurrency };
  };

  // A non-blank COD draft that isn't a positive finite number blocks the
  // create - a COD of 0 makes no sense (pick the plain option instead) and a
  // silent fallback would collect the wrong amount behind the merchant's back.
  const codDraftInvalid = (() => {
    const raw = codDraft.trim();
    if (raw === '') return false;
    const parsed = Number(raw);
    return !Number.isFinite(parsed) || parsed <= 0;
  })();

  // Blank draft = no override; the backend collects the full order total.
  const codAmountForPayload = (): number | undefined => {
    const raw = codDraft.trim();
    if (raw === '') return undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  // Wherever the amount has no prefill (currency mismatch, split parcel) an
  // empty field must block the create: the backend would otherwise fall back
  // to collecting the whole order total.
  const codAmountMissing = defaultCodAmount === '' && codDraft.trim() === '';

  // An unknown order total also counts as "differs" - a hand-typed amount
  // with no baseline deserves the same confirmation (`?? NaN` matters:
  // Number(null) is 0, not NaN). On a currency mismatch the comparison is
  // meaningless (and entry is always deliberate), so the confirm is skipped.
  const codAmountDiffersFromTotal = (() => {
    // A split parcel is meant to collect a part of the order, and the extra-
    // collection confirm already covers it - comparing against the full total
    // would only add a second dialog saying the obvious.
    if (codCurrencyMismatch || isSplitDraft) return false;
    const amount = codAmountForPayload();
    if (amount === undefined) return false;
    const total = Number(order.shopify_total_price ?? NaN);
    return !Number.isFinite(total) || Math.abs(amount - total) > 0.005;
  })();

  const submitShipment = (cashOnDelivery: boolean) => {
    if (insuranceDraftInvalid) {
      toast.error(t('detail.insuranceInvalid'));
      return;
    }
    // Re-asserted here (not only on the Create button / createShipment): the
    // COD-dialog retries enter submitShipment directly, and a money guard must
    // never rely on the modal lock alone.
    if (cashOnDelivery && codCurrencyUnknown) {
      toast.error(t('detail.codCurrencyUnknownHint'));
      return;
    }
    if (cashOnDelivery && (codDraftInvalid || codAmountMissing)) {
      toast.error(t('detail.codAmountInvalid'));
      return;
    }

    const pointId = requiresPoint ? pointDraft.trim() : '';
    const insurance = insuranceForPayload();
    const codAmount = cashOnDelivery ? codAmountForPayload() : undefined;

    createMutation.mutate(
      {
        package_template_key: selectedPackageKey,
        sending_method: sendingMethod,
        delivery_method: deliveryMethod,
        cash_on_delivery: cashOnDelivery,
        ...(codAmount !== undefined && codCurrency
          ? { cash_on_delivery_amount: codAmount, cash_on_delivery_currency: codCurrency }
          : {}),
        ...(insurance ? { insurance } : {}),
        // Both merchant answers live in refs, not arguments: the two guards
        // chain (a paid order whose sibling already collects asks both
        // questions), and a retry that dropped the earlier answer would walk
        // straight back into the dialog it just came from.
        ...(forceCodRef.current ? { force_cash_on_delivery: true } : {}),
        ...(confirmedExtraCodAmountRef.current === codDraft.trim() &&
        confirmedExtraCodAmountRef.current !== null
          ? { confirm_extra_cash_on_delivery: true }
          : {}),
        ...(pointId ? { point_id: pointId } : {}),
      },
      {
        onSuccess: (created) => {
          // The backend legitimately strips a requested COD (unusable total or
          // collection currency) - the merchant must hear about it, not just a
          // generic success.
          if (cashOnDelivery && !created.cod) {
            toast.warning(t('detail.codNotAttached'));
          } else if (
            cashOnDelivery &&
            codAmount !== undefined &&
            created.cod &&
            Math.abs(Number(created.cod.amount) - codAmount) > 0.005
          ) {
            // Defense in depth: the backend applies the sent amount verbatim
            // today, but a collected sum diverging from what the merchant
            // typed must never pass as a plain success.
            toast.warning(t('detail.codAmountNotApplied'));
          } else {
            toast.success(t('detail.shipmentCreated'));
          }
          onCreated?.(created);
        },
        onError: (error) => {
          const problem = toApiProblem(error);
          // COD requested on an already-paid order: the backend refuses with a
          // dedicated problem so the merchant decides - ask via the dialog
          // instead of showing an error toast.
          if (problem.type === 'cod_payment_settled') {
            setCodPaidConfirmOpen(true);
            return;
          }
          // The order started collecting after this page cached it: the backend
          // names what it already collects, and the box switches to split rules
          // (blank amount, no order-total fallback) behind the same dialog.
          if (problem.type === 'cod_already_collected') {
            setExtraCodProblem(problem.detail ?? problem.title);
            setCodExtraConfirmOpen(true);
            return;
          }
          toastProblem(problem);
        },
      },
    );
  };

  const createShipment = async () => {
    // Checked again in submitShipment (for the COD-dialog retries); here it
    // also stops the point-apply side effect from running on a doomed create.
    if (insuranceDraftInvalid) {
      toast.error(t('detail.insuranceInvalid'));
      return;
    }
    // An unfilled amount is named on click rather than by a standing hint under
    // the field (the same message submitShipment falls back on).
    if (isCodOption(deliveryOption) && (codDraftInvalid || codAmountMissing)) {
      toast.error(t('detail.codAmountInvalid'));
      return;
    }

    // The order already collects on another shipment: the buyer would pay both
    // amounts at delivery, so say it out loud before creating. Asked again
    // whenever the amount changes - the consent was for the sum they read, and
    // the amount-differs confirm below is off for split drafts, so this is the
    // only thing standing between an edited figure and the courier.
    if (
      isCodOption(deliveryOption) &&
      addsExtraCollection &&
      confirmedExtraCodAmountRef.current !== codDraft.trim()
    ) {
      setCodExtraConfirmOpen(true);
      return;
    }

    // A COD amount edited away from the order total is asked about first -
    // mirroring the settled-payment confirm - so a typo never reaches the
    // courier's collection.
    if (isCodOption(deliveryOption) && codAmountDiffersFromTotal) {
      setCodAmountConfirmOpen(true);
      return;
    }

    await proceedWithCreate();
  };

  const proceedWithCreate = async () => {
    const pointId = requiresPoint ? pointDraft.trim() : '';

    // First shipment on the order's own delivery method: persist a changed point
    // to the order so it becomes the canonical selection. Split, overridden and
    // already-created shipments never touch it.
    if (pointUpdatesOrder && pointId && pointId !== order.selected_point_id) {
      try {
        await updatePoint.mutateAsync({ point_id: pointId });
      } catch (error) {
        toastProblem(toApiProblem(error));
        return;
      }
    }

    submitShipment(isCodOption(deliveryOption));
  };

  const renderDeliveryOption = (option: DeliveryOption) => (
    <SelectItem
      key={option}
      value={option}
      disabled={
        sendingMethodsFor(baseServiceType(option)).length === 0 ||
        (isCodOption(option) && !orderCodAvailable)
      }
    >
      {tShipments(`serviceTypes.${option}`)}
    </SelectItem>
  );

  return (
    <div
      ref={boxRef}
      className={cn(
        'relative flex w-full flex-col gap-6 rounded-lg bg-[#f8f8f8] p-[30px] pb-[22px] transition-shadow',
        flash && 'ring-2 ring-primary ring-offset-2',
      )}
    >
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={createMutation.isPending}
          aria-label={t('detail.removeShipment')}
          title={t('detail.removeShipment')}
          className="absolute -right-12 top-6 grid size-9 place-items-center rounded-[10px] border border-dashed border-black bg-[#f2f2f2] hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
        >
          <Trash2 className="size-5" />
        </button>
      ) : null}

      <div className="flex flex-wrap items-start gap-[26px]">
        <Field label={t('detail.deliveryMethod')} className="w-[159px]">
          {isDraft ? (
            <Select
              value={deliveryOption}
              onValueChange={(v) => changeDeliveryOption(v as DeliveryOption)}
            >
              <SelectTrigger className={selectTriggerClasses}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>{PLAIN_DELIVERY_OPTIONS.map(renderDeliveryOption)}</SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>
                    {tShipments(
                      orderCodAvailable
                        ? 'serviceTypeGroups.cod'
                        : 'serviceTypeGroups.codUnavailable',
                    )}
                  </SelectLabel>
                  {COD_DELIVERY_OPTIONS.map(renderDeliveryOption)}
                </SelectGroup>
              </SelectContent>
            </Select>
          ) : (
            <StaticSelect value={shipmentDeliveryDisplay} />
          )}
        </Field>

        {requiresPoint ? (
          <Field label={t('detail.parcelLocker')} className="w-[248px]">
            <div className="flex items-center gap-[26px]">
              <div className="flex w-[186px]">
                <input
                  value={isDraft ? pointDraft : (shipment.destination_point_id ?? '')}
                  onChange={(e) => setPointDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && pointUpdatesOrder) applyPoint();
                  }}
                  disabled={!isDraft || updatePoint.isPending}
                  className={cn(
                    'h-9 w-full min-w-0 border border-[#e5e5e5] bg-white px-3 text-sm text-[#737373] focus-visible:outline-none disabled:opacity-70',
                    pointUpdatesOrder ? 'rounded-l-[10px] border-r-0' : 'rounded-[10px]',
                  )}
                />
                {/* "Apply" only persists to the order for the first shipment on the
                    order's own delivery method; split/overridden drafts keep the
                    point local and use it at creation time. */}
                {pointUpdatesOrder ? (
                  <button
                    type="button"
                    onClick={applyPoint}
                    disabled={!isDraft || updatePoint.isPending}
                    aria-label={t('detail.applyPoint')}
                    title={t('detail.applyPoint')}
                    className="grid size-9 shrink-0 place-items-center rounded-r-[10px] border border-[#e5e5e5] bg-white hover:bg-accent disabled:pointer-events-none disabled:opacity-70"
                  >
                    <Check className="size-4" />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setMapOpen(true)}
                disabled={!isDraft}
                aria-label={t('detail.pickOnMap')}
                title={t('detail.pickOnMap')}
                className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-black bg-transparent hover:bg-accent disabled:opacity-50"
              >
                <PointSearchIcon className="size-[22px]" />
              </button>
            </div>
            {order.point_auto_selected ? (
              <span className="inline-flex w-fit items-center rounded-full bg-[#FFF2BF] px-2.5 py-0.5 text-xs font-medium text-[#1a1a1a]">
                {t('detail.pointAutoSelected')}
              </span>
            ) : null}
          </Field>
        ) : null}

        <Field label={t('detail.packageSize')} className="w-[233px]">
          {isDraft ? (
            <>
              <Select
                value={selectedPackageKey}
                onValueChange={setTemplateKey}
                disabled={customPackages.isLoading || packageOptions.length === 0}
              >
                <SelectTrigger className={selectTriggerClasses}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <TooltipProvider delayDuration={200}>
                    {packageTemplates.length ? (
                      <SelectGroup>
                        {packageTemplates.map((template) => (
                          <PackageOption
                            key={template.key}
                            pkg={template}
                            disabledReason={packageTooBigReason(template)}
                          />
                        ))}
                      </SelectGroup>
                    ) : null}
                    {customPackages.data?.length ? (
                      <>
                        {packageTemplates.length ? <SelectSeparator /> : null}
                        <SelectGroup>
                          <SelectLabel>{tShipments('packageGroups.custom')}</SelectLabel>
                          {customPackages.data.map((pkg) => (
                            <PackageOption
                              key={pkg.key}
                              pkg={pkg}
                              disabledReason={packageTooBigReason(pkg)}
                            />
                          ))}
                        </SelectGroup>
                      </>
                    ) : null}
                  </TooltipProvider>
                </SelectContent>
              </Select>
              {noPackageAvailable ? (
                <span className="text-xs text-amber-700">
                  {packageOptions.length === 0
                    ? t('detail.noPackagesForDestination')
                    : t('detail.noPackageFits', {
                        method: tShipments(`serviceTypes.${deliveryMethod}`),
                      })}
                </span>
              ) : null}
            </>
          ) : (
            <StaticSelect
              value={`${shipment.parcel.dimensions.height} x ${shipment.parcel.dimensions.width} x ${shipment.parcel.dimensions.length} ${shipment.parcel.dimensions.unit.toLowerCase()}`}
            />
          )}
        </Field>

        <Field label={t('detail.sendingMethod')} className="w-[159px]">
          {isDraft ? (
            <Select
              value={sendingMethod}
              onValueChange={(v) => {
                sendingTouchedRef.current = true;
                setSendingMethod(v as SendingMethod);
              }}
            >
              <SelectTrigger className={selectTriggerClasses}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALL_SENDING_METHODS.map((method) => (
                  <SelectItem
                    key={method}
                    value={method}
                    disabled={
                      sendingMethodsFor(deliveryMethod).length > 0 &&
                      !sendingMethodsFor(deliveryMethod).includes(method)
                    }
                  >
                    {tShipments(`create.sendingMethods.${method}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <StaticSelect
              value={
                shipment.sending_method
                  ? tShipments(`create.sendingMethods.${shipment.sending_method}`)
                  : '-'
              }
            />
          )}
        </Field>

        <Field label={tShipments('detail.insurance')} className="w-[159px]">
          {isDraft ? (
            <>
              <div
                className={cn(
                  'flex h-9 w-full items-center rounded-[10px] border bg-white',
                  insuranceDraftInvalid ? 'border-destructive' : 'border-[#e5e5e5]',
                )}
              >
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.01}
                  placeholder={orderInsuranceAvailable ? '0.00' : '-'}
                  value={insuranceDraft}
                  onChange={(e) => {
                    insuranceTouchedRef.current = true;
                    setInsuranceDraft(e.target.value);
                  }}
                  disabled={!orderInsuranceAvailable}
                  aria-label={tShipments('detail.insurance')}
                  className="h-full w-full min-w-0 rounded-l-[10px] bg-transparent px-3 text-sm text-[#737373] focus-visible:outline-none disabled:opacity-70"
                />
                {insuranceCurrency && orderInsuranceAvailable ? (
                  <span className="pr-3 text-sm text-[#737373]">{insuranceCurrency}</span>
                ) : null}
              </div>
              {/* The corridor offers no cover at all - nothing the merchant
                  types can change that, so the field keeps a standing hint. */}
              {!orderInsuranceAvailable ? (
                <span className="text-xs leading-4 text-[#737373]">
                  {t('detail.insuranceUnavailableHint')}
                </span>
              ) : null}
            </>
          ) : (
            // Same input-with-currency look as the draft, just read-only - not
            // a fake select like the other created-state fields.
            <div className="flex h-9 w-full items-center rounded-[10px] border border-[#e5e5e5] bg-white">
              <input
                value={
                  shipment.insurance
                    ? String(shipment.insurance.amount)
                    : tShipments('detail.noInsurance')
                }
                readOnly
                disabled
                aria-label={tShipments('detail.insurance')}
                className="h-full w-full min-w-0 rounded-l-[10px] bg-transparent px-3 text-sm text-[#737373]"
              />
              {shipment.insurance ? (
                <span className="pr-3 text-sm text-[#737373]">{shipment.insurance.currency}</span>
              ) : null}
            </div>
          )}
        </Field>

        {(isDraft ? isCodOption(deliveryOption) : Boolean(shipment.cod)) ? (
          <Field label={tShipments('detail.cashOnDelivery')} className="w-[159px]">
            {isDraft ? (
              <>
                <div
                  className={cn(
                    'flex h-9 w-full items-center rounded-[10px] border bg-white',
                    codDraftInvalid ? 'border-destructive' : 'border-[#e5e5e5]',
                  )}
                >
                  <input
                    ref={codInputRef}
                    type="number"
                    inputMode="decimal"
                    min={0.01}
                    step={0.01}
                    placeholder="0.00"
                    value={codDraft}
                    onChange={(e) => {
                      codTouchedRef.current = true;
                      setCodDraft(e.target.value);
                    }}
                    disabled={codCurrencyUnknown}
                    aria-label={tShipments('detail.cashOnDelivery')}
                    className="h-full w-full min-w-0 rounded-l-[10px] bg-transparent px-3 text-sm text-[#737373] focus-visible:outline-none disabled:opacity-70"
                  />
                  {codCurrency ? (
                    <span className="pr-3 text-sm text-[#737373]">{codCurrency}</span>
                  ) : null}
                </div>
                {/* An unknown collection currency cannot be typed around, so it
                    keeps its standing explanation; a missing amount is named on
                    click instead (the field carries its own currency suffix). */}
                {codCurrencyUnknown ? (
                  <span className="text-xs leading-4 text-[#737373]">
                    {t('detail.codCurrencyUnknownHint')}
                  </span>
                ) : null}
              </>
            ) : (
              <div className="flex h-9 w-full items-center rounded-[10px] border border-[#e5e5e5] bg-white">
                <input
                  value={shipment.cod ? String(shipment.cod.amount) : ''}
                  readOnly
                  disabled
                  aria-label={tShipments('detail.cashOnDelivery')}
                  className="h-full w-full min-w-0 rounded-l-[10px] bg-transparent px-3 text-sm text-[#737373]"
                />
                {shipment.cod ? (
                  <span className="pr-3 text-sm text-[#737373]">{shipment.cod.currency}</span>
                ) : null}
              </div>
            )}
          </Field>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-[49px]">
        <div className="flex flex-col gap-[10px]">
          <span className={cn(fieldLabelClasses, 'whitespace-nowrap')}>
            {t('detail.shipmentStatus')}
          </span>
          {isDraft ? (
            <span className="inline-flex h-6 w-fit items-center justify-center gap-1 whitespace-nowrap rounded-[10px] bg-[#dddddd] px-2 text-sm font-medium leading-5 text-foreground">
              {tShipments('statusGroups.new')}
            </span>
          ) : (
            <StatusBadge
              group={shipment.carrier_status_group ?? 'new'}
              code={shipment.carrier_status}
              className="w-fit"
            />
          )}
        </div>

        <div className="flex min-w-[200px] flex-1 flex-col gap-3">
          <span className={fieldLabelClasses}>{t('detail.shipmentNumber')}</span>
          <input
            value={shipment?.tracking_number ?? ''}
            readOnly
            disabled
            className="h-9 w-full rounded-[10px] border border-[#e5e5e5] bg-[#f2f2f2] px-3 text-sm text-foreground"
          />
        </div>

        {isDraft ? (
          <button
            type="button"
            onClick={() => void createShipment()}
            disabled={
              createMutation.isPending ||
              updatePoint.isPending ||
              customPackages.isLoading ||
              selectedPackageKey === '' ||
              (isCodOption(deliveryOption) && codCurrencyUnknown)
            }
            className={yellowButtonClasses}
          >
            {t('detail.createShipment')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setDownloadOpen(true)}
            className={yellowButtonClasses}
          >
            {t('detail.downloadLabel')}
          </button>
        )}
      </div>

      <AlertDialog open={codAmountConfirmOpen} onOpenChange={setCodAmountConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.codAmountConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.codAmountConfirm.description', {
                amount: codDraft.trim(),
                total: order.shopify_total_price ?? '',
                currency: codCurrency ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void proceedWithCreate()}>
              {t('detail.codAmountConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={codExtraConfirmOpen} onOpenChange={setCodExtraConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.codExtra.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {/* When the backend caught it, its own sentence names the
                  collection this page never saw - ours would state a sum it
                  cannot know. */}
              {orderCodCollected
                ? t('detail.codExtra.description', {
                    collected: collectedCodAmount.toFixed(2),
                    amount: (codAmountForPayload() ?? 0).toFixed(2),
                    total: (collectedCodAmount + (codAmountForPayload() ?? 0)).toFixed(2),
                    currency: orderCodCollected.currency,
                  })
                : extraCodProblem}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const acknowledged = codDraft.trim();
                // Reached with an empty field on the backend-driven path: the
                // 422 blanked it as it opened this dialog. Nothing to consent
                // to yet - send them to the field instead of pretending this
                // created something.
                if (acknowledged === '') {
                  toast.error(t('detail.codAmountInvalid'));
                  codInputRef.current?.focus();

                  return;
                }
                confirmedExtraCodAmountRef.current = acknowledged;
                void proceedWithCreate();
              }}
            >
              {t('detail.codExtra.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={codPaidConfirmOpen} onOpenChange={setCodPaidConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.codPaid.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.codPaid.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => submitShipment(false)}
              className="border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
            >
              {t('detail.codPaid.without')}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => {
                forceCodRef.current = true;
                submitShipment(true);
              }}
            >
              {t('detail.codPaid.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LabelFormatDialog
        shipments={downloadOpen && shipment ? [shipment] : null}
        onClose={() => setDownloadOpen(false)}
      />

      {requiresPoint ? (
        <PointMapDialog
          order={order}
          open={mapOpen}
          onOpenChange={setMapOpen}
          {...(pointUpdatesOrder ? {} : { onSelect: setPointDraft })}
        />
      ) : null}
    </div>
  );
}
